import React, { useEffect, useRef } from "react";
import Resources from "../../../types/Resources";
import { CounterSignatureRequestInputManager } from "../../../hooks/useCounterSignatureRequestInput";
import Button from "../../Common/Button";
import { CounterSignatureRequestRecipient_toString } from "../../../types/CounterSignatureRequestRecipient";

type CounterSignatureRequestInputProps = {
    resources: Resources;
    counterSignatureRequestInputHandler: CounterSignatureRequestInputManager;
    inputId: string;
    containerClassName?: string;
};

function CounterSignatureRequestInput({
    resources,
    counterSignatureRequestInputHandler,
    inputId,
    containerClassName = ""
}: CounterSignatureRequestInputProps) {
    const searchSectionRef = useRef<HTMLDivElement>(null);
    const activeRecipientId = `${inputId}__active-recipient`;

    useEffect(() => {
        function clickHandler(e: MouseEvent) {
            if (!searchSectionRef.current || !searchSectionRef.current.contains(e.target as Node)) {
                counterSignatureRequestInputHandler.closeResultsDropdownHandler();
            }
        }

        document.addEventListener("mousedown", clickHandler);
        return () => document.removeEventListener("mousedown", clickHandler);
    }, [counterSignatureRequestInputHandler.closeResultsDropdownHandler]);

    const searchResultListItems = counterSignatureRequestInputHandler.searchResultRecipients.map((r) => (
        <li
            key={r.companyDepartmentName + r.companyName + r.userId}
            onClick={() => counterSignatureRequestInputHandler.searchResultListItemClickHandler(r)}
            className="list-group-item p-2 border-top"
            tabIndex={0}
            onKeyDown={(e) => counterSignatureRequestInputHandler.searchResultListItemKeyDownHandler(e, r)}
        >
            {CounterSignatureRequestRecipient_toString(r)}
        </li>
    ));

    return (
        <div className={`${containerClassName}`}>
            {counterSignatureRequestInputHandler.hasActiveCounterSignatureRequest ? (
                <div>
                    <label className="fw-bold form-label" htmlFor={activeRecipientId}>
                        {resources.get("ExternalPersonToCounterSign")}
                    </label>
                    <div className="input-group">
                        <input
                            id={activeRecipientId}
                            className="form-control"
                            type="text"
                            value={counterSignatureRequestInputHandler.activeRecipientText}
                            disabled
                        />
                        {counterSignatureRequestInputHandler.canCancelCounterSignatureRequest ? (
                            <Button
                                variant="danger"
                                onClick={counterSignatureRequestInputHandler.cancelButtonClickHandlerAsync}
                                disabled={counterSignatureRequestInputHandler.disableCancelButton}
                            >
                                {resources.get("CancelCounterSignature")}
                            </Button>
                        ) : null}
                    </div>
                </div>
            ) : counterSignatureRequestInputHandler.selectedRecipient ? (
                <div>
                    <label className="fw-bold form-label" htmlFor={inputId}>
                        {resources.get("ExternalPersonToCounterSign")}
                    </label>
                    <div className="input-group">
                        <div
                            id={inputId}
                            className="form-control ps-0"
                            style={{ backgroundColor: "#E9ECEF", overflow: "hidden" }}
                        >
                            <span
                                className="p-2 border rounded cursor-pointer bg-white"
                                onClick={counterSignatureRequestInputHandler.unselectRecipientHandler}
                            >
                                <span className="me-2">
                                    {CounterSignatureRequestRecipient_toString(
                                        counterSignatureRequestInputHandler.selectedRecipient
                                    )}
                                </span>
                                <i className="fas fa-xmark"></i>
                            </span>
                        </div>
                        <Button
                            onClick={counterSignatureRequestInputHandler.sendButtonClickHandlerAsync}
                            disabled={counterSignatureRequestInputHandler.disableSendButton}
                        >
                            {resources.get("SendCounterSignatureRequest")}
                        </Button>
                    </div>
                </div>
            ) : counterSignatureRequestInputHandler.canCreateCounterSignatureRequest ? (
                <div className="form-group position-relative" ref={searchSectionRef}>
                    <label className="fw-bold form-label" htmlFor={inputId}>
                        {resources.get("ExternalPersonToCounterSign")}
                    </label>
                    <div className="input-group">
                        <input
                            disabled={counterSignatureRequestInputHandler.disableInput}
                            id={inputId}
                            className="form-control"
                            type="text"
                            value={counterSignatureRequestInputHandler.searchInput}
                            onChange={(e) =>
                                counterSignatureRequestInputHandler.searchInputChangeHandler(e.target.value)
                            }
                        />
                        <Button
                            disabled={counterSignatureRequestInputHandler.disableSendButton}
                            onClick={counterSignatureRequestInputHandler.sendButtonClickHandlerAsync}
                        >
                            {resources.get("SendCounterSignatureRequest")}
                        </Button>
                    </div>
                    {counterSignatureRequestInputHandler.showResultsDropdown ? (
                        <div
                            className="position-absolute w-100 border rounded p-0 bg-white"
                            style={{ maxHeight: "300px", overflowY: "auto", zIndex: 100 }}
                        >
                            {searchResultListItems.length > 0 ? (
                                <ul className="list-unstyled search-results m-0">{searchResultListItems}</ul>
                            ) : (
                                <div className="p-2">{resources.get("NoRecipientSearchResults")}</div>
                            )}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

export default CounterSignatureRequestInput;
